import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { api, ErroHttp } from '@/lib/api';
import type { Produto } from '@wms/shared';
import type {
  BuscaResposta,
  CorredoresResposta,
  DashboardResposta,
  Mapa3DResposta,
  MapaResposta,
  OcupacaoEmpresaResposta,
  CanceladosResposta,
  ExpedicaoResposta,
  FaturamentoPorHoraResposta,
  GalpaoMeli,
  PainelPedidosResposta,
  SerieEmpresaResposta,
  PosicaoResposta,
  TimelineResposta,
} from '@/lib/tipos';

/** Erros de permissão e autenticação não se resolvem repetindo a chamada. */
function tentarDeNovo(tentativas: number, erro: unknown): boolean {
  if (erro instanceof ErroHttp && (erro.naoAutenticado || erro.semPermissao || erro.status === 400)) {
    return false;
  }
  return tentativas < 2;
}

/**
 * O mapa inteiro do centro vem numa chamada só (12.311 posições ≈ 1,5 MB).
 * Mantemos em cache por 5 min e reaproveitamos o MESMO dado na lista e nos
 * filtros: buscar de novo a cada troca de visualização seria exatamente a
 * morosidade que o projeto quer eliminar.
 */
export function useMapa(centro: number | null): UseQueryResult<MapaResposta, unknown> {
  return useQuery<MapaResposta>({
    queryKey: ['mapa', centro],
    queryFn: () => api.get<MapaResposta>('/api/mapa', { centro }),
    enabled: centro !== null,
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
    retry: tentarDeNovo,
  });
}

/** Um produto numa posição: uma linha da lista de Estoque e do Excel dela. */
export interface SkuNaPosicao {
  localizador: number;
  produto: number;
  descricao: string;
  ean: string | null;
  saldo: number;
}

/**
 * Os produtos de cada posição do centro. A lista do Estoque abre cada posição
 * em uma linha por SKU, e o "Exportar CSV" baixa essas mesmas linhas. Só
 * carrega com a lista aberta, como o mapa completo.
 */
export function useSkusPorPosicao(
  centro: number | null,
): UseQueryResult<Map<number, SkuNaPosicao[]>, unknown> {
  return useQuery({
    queryKey: ['skus-por-posicao', centro],
    queryFn: async () => {
      const r = await api.get<{ itens: SkuNaPosicao[] }>('/api/estoque/skus-por-posicao', { centro });
      const porPosicao = new Map<number, SkuNaPosicao[]>();
      for (const i of r.itens) {
        const lista = porPosicao.get(i.localizador);
        if (lista) lista.push(i);
        else porPosicao.set(i.localizador, [i]);
      }
      return porPosicao;
    },
    enabled: centro !== null,
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
    retry: tentarDeNovo,
  });
}

/**
 * Planta do CD: os corredores agregados com ocupação, para o zoom afastado.
 * Chamada leve (uma linha por rua), então pode carregar assim que há centro.
 */
export function useCorredores(
  centro: number | null,
): UseQueryResult<CorredoresResposta, unknown> {
  return useQuery<CorredoresResposta>({
    queryKey: ['corredores', centro],
    queryFn: () => api.get<CorredoresResposta>('/api/corredores', { centro }),
    enabled: centro !== null,
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
    retry: tentarDeNovo,
  });
}

/**
 * Rack 3D de um corredor: cada vaga com geometria, produtos e ocupação
 * volumétrica. É a fonte do mapa isométrico aprovado no protótipo.
 */
export function useMapa3D(
  centro: number | null,
  corredor: string | null,
): UseQueryResult<Mapa3DResposta, unknown> {
  const alvo = corredor?.trim() ?? '';
  return useQuery<Mapa3DResposta>({
    queryKey: ['mapa3d', centro, alvo.toUpperCase()],
    queryFn: () => api.get<Mapa3DResposta>('/api/mapa3d', { centro, corredor: alvo }),
    enabled: centro !== null && alvo.length > 0,
    /*
     * Mantém a rua anterior na tela enquanto a próxima carrega.
     *
     * Sem isso, paginar de rua fazia `isLoading` virar true, a tela trocava o
     * rack por um esqueleto e o `Rack3D` DESMONTAVA — levando junto o estado
     * de quem estava olhando: a tela cheia caía (o elemento em fullscreen
     * deixa de existir), o painel recolhia, o zoom e a rolagem zeravam. Quem
     * percorre a rua 1, 2, 3 atrás de um SKU perdia o enquadramento a cada
     * passo.
     *
     * Com o dado anterior de placeholder o componente permanece montado: o
     * desenho antigo fica à vista por um instante e é substituído quando o
     * novo chega. `isFetching` continua dizendo que há carga em andamento,
     * para a tela poder avisar.
     */
    placeholderData: (anterior) => anterior,
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
    retry: tentarDeNovo,
  });
}

/**
 * O painel do centro.
 *
 * `comSerie` PEDE a série de 30 dias, que custa ~3,1 s e só a tela de Análises
 * desenha. A tela inicial tem o gráfico dela em `useSerieEmpresa` e nunca leu
 * `serieOcupacao` — mas pagava por ela em toda abertura. Sem o campo, o resto
 * do painel responde em centenas de milissegundos.
 *
 * A flag entra na `queryKey` de propósito: são duas respostas diferentes, e
 * compartilhar a chave faria a home servir à Análises um corpo sem a série.
 */
export function useDashboard(
  centro: number | null,
  comSerie = false,
): UseQueryResult<DashboardResposta, unknown> {
  return useQuery<DashboardResposta>({
    queryKey: ['dashboard', centro, comSerie ? 'com-serie' : 'sem-serie'],
    queryFn: () =>
      api.get<DashboardResposta>('/api/dashboard', {
        centro,
        ...(comSerie ? { serie: 1 } : {}),
      }),
    enabled: centro !== null,
    staleTime: 60_000,
    retry: tentarDeNovo,
  });
}

/**
 * Ocupação de todos os centros da empresa, numa chamada só.
 *
 * Fica separada do dashboard de propósito: ela custa ~500ms (14 centros do
 * histórico de 94M linhas) e o dashboard do centro não pode esperar por ela
 * para pintar. Assim a tela abre com o centro e a consolidação entra depois,
 * sem segurar o resto.
 */
export function useOcupacaoEmpresa(
  empresa: number | null,
): UseQueryResult<OcupacaoEmpresaResposta, unknown> {
  return useQuery<OcupacaoEmpresaResposta>({
    queryKey: ['ocupacao-empresa', empresa],
    queryFn: () => api.get<OcupacaoEmpresaResposta>('/api/empresa/ocupacao', { empresa }),
    enabled: empresa !== null,
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
    retry: tentarDeNovo,
  });
}

/**
 * Série de 30 dias da empresa, para os estoques marcados.
 *
 * A chave inclui a lista de centros, então cada combinação é buscada uma vez
 * e fica em cache: marcar e desmarcar volta instantâneo depois da primeira
 * vez. `staleTime` alto porque o dado é fechamento — imutável até as 04:00.
 */
export function useSerieEmpresa(
  empresa: number | null,
  /**
   * Quais estoques somar. `undefined` deixa a decisão com a API, que usa os
   * marcados como "contabiliza posições" no cadastro — é o caso de quem só
   * quer "a empresa", sem carregar a lista antes. Lista VAZIA é diferente de
   * ausente: significa que o usuário desmarcou tudo, e aí não há o que buscar.
   */
  centros?: readonly number[] | undefined,
): UseQueryResult<SerieEmpresaResposta, unknown> {
  const lista = centros === undefined ? null : [...centros].sort((a, b) => a - b).join(',');
  return useQuery<SerieEmpresaResposta>({
    queryKey: ['serie-empresa', empresa, lista ?? 'cadastro'],
    queryFn: () =>
      api.get<SerieEmpresaResposta>('/api/empresa/ocupacao/serie', {
        empresa,
        ...(lista === null ? {} : { centros: lista }),
      }),
    enabled: empresa !== null && lista !== '',
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
    retry: tentarDeNovo,
  });
}

/** As empresas com conta do Mercado Livre cadastrada e liberada para o usuário. */
export function useGalpoesMeli(): UseQueryResult<{ galpoes: GalpaoMeli[] }, unknown> {
  return useQuery<{ galpoes: GalpaoMeli[] }>({
    queryKey: ['meli-galpoes'],
    queryFn: () => api.get<{ galpoes: GalpaoMeli[] }>('/api/meli/galpoes'),
    staleTime: 30 * 60_000,
    retry: tentarDeNovo,
  });
}

/**
 * O painel de expedição de uma empresa.
 *
 * Carga pesada de propósito: varre o ML e consulta o prazo de cada envio
 * pendente. O servidor guarda 60s; aqui o `staleTime` acompanha, para trocar
 * de empresa e voltar não disparar tudo de novo.
 */
export function useExpedicaoMeli(empresa: number | null): UseQueryResult<ExpedicaoResposta, unknown> {
  return useQuery<ExpedicaoResposta>({
    queryKey: ['meli-expedicao', empresa],
    queryFn: () => api.get<ExpedicaoResposta>('/api/meli/expedicao', { empresa }),
    enabled: empresa !== null,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    retry: tentarDeNovo,
  });
}

/**
 * Os cancelados do ML que a operação já comprometeu.
 *
 * Bem mais barata que o painel — duas varreduras e uma consulta, sem SLA —,
 * mas com o mesmo cache curto: é a aba que se reabre a cada meia hora.
 */
export function useCanceladosMeli(
  empresa: number | null,
  dias: number,
): UseQueryResult<CanceladosResposta, unknown> {
  return useQuery<CanceladosResposta>({
    queryKey: ['meli-cancelados', empresa, dias],
    queryFn: () => api.get<CanceladosResposta>('/api/meli/cancelados', { empresa, dias }),
    enabled: empresa !== null,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    retry: tentarDeNovo,
  });
}

export function usePosicao(
  centro: number | null,
  localizador: number | null,
): UseQueryResult<PosicaoResposta, unknown> {
  return useQuery<PosicaoResposta>({
    queryKey: ['posicao', centro, localizador],
    queryFn: () => api.get<PosicaoResposta>(`/api/posicao/${localizador}`, { centro }),
    enabled: centro !== null && localizador !== null,
    staleTime: 60_000,
    retry: tentarDeNovo,
  });
}

export function useTimeline(
  centro: number | null,
  localizador: number | null,
  habilitada: boolean,
): UseQueryResult<TimelineResposta, unknown> {
  return useQuery<TimelineResposta>({
    queryKey: ['timeline', centro, localizador],
    queryFn: () => api.get<TimelineResposta>(`/api/posicao/${localizador}/timeline`, { centro }),
    // Só busca quando o usuário abre a aba: a timeline é a consulta mais cara.
    enabled: habilitada && centro !== null && localizador !== null,
    staleTime: 60_000,
    retry: tentarDeNovo,
  });
}

export function useBusca(
  centro: number | null,
  termo: string,
): UseQueryResult<BuscaResposta, unknown> {
  const limpo = termo.trim();
  return useQuery<BuscaResposta>({
    queryKey: ['busca', centro, limpo],
    queryFn: () => api.get<BuscaResposta>('/api/busca', { centro, q: limpo }),
    enabled: centro !== null && limpo.length >= 2,
    staleTime: 60_000,
    retry: tentarDeNovo,
  });
}

/**
 * Ficha do produto. Cache longo: catálogo muda muito menos que estoque, e a
 * mesma ficha é reaproveitada pela gaveta de posição, pela lista e pelos
 * negativos.
 */
export function useProduto(sku: number | null): UseQueryResult<Produto, unknown> {
  return useQuery<Produto>({
    queryKey: ['produto', sku],
    queryFn: () => api.get<Produto>(`/api/produto/${sku}`),
    enabled: sku !== null && Number.isFinite(sku),
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
    // 404 é resposta, não falha: SKU que não existe não passa a existir na
    // segunda tentativa.
    retry: (tentativas, erro) =>
      !(erro instanceof ErroHttp && (erro.status === 404 || erro.naoAutenticado || erro.semPermissao)) &&
      tentativas < 2,
  });
}

/**
 * Pedidos da empresa na esteira — o bloco de vendas da tela inicial.
 *
 * Soma as MESMAS empresas que o painel de expedição soma (o `EMPRESAS` de
 * `WMS.MELI_GALPAO`), então o número daqui bate com o de lá. O servidor guarda
 * 60s; o `staleTime` acompanha, para ir e voltar da tela inicial não refazer
 * três agrupamentos sobre TELEVENDAS.
 */
export function usePainelPedidos(
  empresa: number | null,
  dias = 3,
): UseQueryResult<PainelPedidosResposta, unknown> {
  return useQuery<PainelPedidosResposta>({
    queryKey: ['pedidos-painel', empresa, dias],
    queryFn: () => api.get<PainelPedidosResposta>('/api/pedidos/painel', { empresa, dias }),
    enabled: empresa !== null,
    /* Acompanha o cache do servidor (5 min): com `staleTime` menor, o React
       Query dispararia uma volta de rede a cada minuto só para receber de
       novo o mesmo corpo que o servidor já tinha guardado. */
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: tentarDeNovo,
  });
}

/**
 * Faturamento de hoje, hora a hora.
 *
 * Consulta própria e não um campo do painel de pedidos: a hora corrente anda
 * em minutos e o funil de 3 dias anda em horas. Separadas, uma falhando não
 * apaga a outra da tela.
 */
export function useFaturamentoPorHora(
  empresa: number | null,
): UseQueryResult<FaturamentoPorHoraResposta, unknown> {
  return useQuery<FaturamentoPorHoraResposta>({
    queryKey: ['faturamento-hora', empresa],
    queryFn: () => api.get<FaturamentoPorHoraResposta>('/api/faturamento/por-hora', { empresa }),
    enabled: empresa !== null,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: tentarDeNovo,
  });
}

/* ==========================================================================
   ATUALIZAR — o botão da tela, e por que não é `refetch()`
   ==========================================================================

   O servidor guarda estas respostas (5 minutos nas de venda, até o fechamento
   nas de estoque). Um `refetch()` do React Query devolveria o MESMO corpo
   guardado: a tela piscaria e o número não mudaria — e quem apertou concluiria
   que o dado está atualizado quando não está.

   Estas funções pedem com `atualizar=1`, que faz o servidor descartar o
   guardado e produzir de novo, e entregam o resultado direto ao cache do
   cliente. Uma ida de rede, não duas: buscar fresco e depois mandar o React
   Query buscar outra vez pagaria a volta duas vezes.

   Ficam ao lado dos hooks de propósito: a URL e a `queryKey` de cada consulta
   têm de continuar num lugar só. Separadas, uma renomeação silenciosa faria o
   botão atualizar uma chave que ninguém lê.
*/

/** As três consultas do bloco de ESTOQUE. */
export async function atualizarEstoque(
  cliente: QueryClient,
  centro: number | null,
  empresa: number | null,
): Promise<void> {
  await Promise.all([
    centro === null
      ? Promise.resolve()
      : api
          .get<DashboardResposta>('/api/dashboard', { centro, atualizar: 1 })
          .then((r) => cliente.setQueryData(['dashboard', centro, 'sem-serie'], r)),
    empresa === null
      ? Promise.resolve()
      : api
          .get<OcupacaoEmpresaResposta>('/api/empresa/ocupacao', { empresa, atualizar: 1 })
          .then((r) => cliente.setQueryData(['ocupacao-empresa', empresa], r)),
    /*
      SEM `centros`, e a chave tem de casar com a de `useSerieEmpresa`.

      A tela deixou de mandar a lista (a seleção manual saiu do cartão de
      ocupação), então a consulta vive na chave 'cadastro'. Enquanto isto aqui
      mandava a lista, o botão buscava dado fresco e o gravava numa chave que
      ninguém lia: a rede era paga e a curva na tela não mudava.
    */
    empresa === null
      ? Promise.resolve()
      : api
          .get<SerieEmpresaResposta>('/api/empresa/ocupacao/serie', { empresa, atualizar: 1 })
          .then((r) => cliente.setQueryData(['serie-empresa', empresa, 'cadastro'], r)),
  ]);
}

/** As duas consultas do bloco de VENDAS. */
export async function atualizarVendas(
  cliente: QueryClient,
  empresa: number | null,
  dias = 3,
): Promise<void> {
  if (empresa === null) return;
  await Promise.all([
    api
      .get<PainelPedidosResposta>('/api/pedidos/painel', { empresa, dias, atualizar: 1 })
      .then((r) => cliente.setQueryData(['pedidos-painel', empresa, dias], r)),
    api
      .get<FaturamentoPorHoraResposta>('/api/faturamento/por-hora', { empresa, atualizar: 1 })
      .then((r) => cliente.setQueryData(['faturamento-hora', empresa], r)),
  ]);
}
